import React, { useState, useEffect, useRef } from 'react';
import { FormattingToolbar } from '../ui/FormattingToolbar';
import { TrashIcon, MinusCircleIcon, StarIcon, StarSolidIcon } from '../ui/Icons';
import { dataUrlToBytes, MAX_IMAGE_BYTES, MAX_GIF_BYTES, fileToDataUrl } from '../../utils/helpers';
import { getConditionSourceLabels } from '../../utils/questionnaireEditorUtils';

export function QuestionEditor({ question, index, displayIndex = index, displayLabel, labelMap = {}, updateQuestion, removeQuestion, handleOptionChange, handleOptionScoreChange, addOption, removeOption, isDragging, onDragStart, onDragEnd, setDraggedIndex, setDragOverIndex, handleOptionDragStart, handleOptionDragOver, handleOptionDragEnd, draggedOptionIndex, dragOverOptionIndex, pageOptions = [], onMoveToPage, conditionalCandidates = [], onConditionalChange }) {
  const textRef = useRef(null); const descriptionRef = useRef(null); const fileInputRef = useRef(null);
  const cardRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadSize, setUploadSize] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [adminNoteOpen, setAdminNoteOpen] = useState(false);
  const isTyping = useRef(false);
  const typingTimeout = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [conditionalOpen, setConditionalOpen] = useState(Boolean(question.conditional));
  const normalizedConditionalCandidates = (conditionalCandidates || [])
    .map((candidate) => ({
      ...candidate,
      options: (candidate.options || [])
        .map((option) => String(option || '').trim())
        .filter(Boolean)
    }));
  const isCandidateUsable = (candidate) =>
    candidate.options.length > 0 &&
    (candidate.type !== 'combobox' || getConditionSourceLabels(candidate).length > 0);
  const conditionalCandidatesWithOptions = normalizedConditionalCandidates.filter(isCandidateUsable);
  const conditionalCandidatesBeforeCurrent = normalizedConditionalCandidates
    .slice(0, Math.max(index, 0))
    .filter(isCandidateUsable);
  const preferredConditionalCandidate = conditionalCandidatesBeforeCurrent.length > 0
    ? conditionalCandidatesBeforeCurrent[conditionalCandidatesBeforeCurrent.length - 1]
    : conditionalCandidatesWithOptions[0] || null;
  const selectedConditionalQuestion = normalizedConditionalCandidates.find(
    (candidate) => String(candidate.id) === String(question?.conditional?.dependsOn)
  ) || preferredConditionalCandidate || normalizedConditionalCandidates[0] || null;
  const selectedConditionalOptions = selectedConditionalQuestion?.options || [];
  const selectedConditionalLabels = getConditionSourceLabels(selectedConditionalQuestion);

  // Builds a conditional config for a candidate parent. For combobox parents it
  // includes requiresLabel (defaulting/snapping to a valid predefined entry);
  // for single-choice parents it omits requiresLabel entirely.
  const makeConditional = (candidate, valueOverride, labelOverride) => {
    if (!candidate) return null;
    const options = (candidate.options || []).map((o) => String(o || '').trim()).filter(Boolean);
    const next = {
      dependsOn: candidate.id,
      requiresValue: valueOverride != null && options.includes(valueOverride) ? valueOverride : options[0],
    };
    const labels = getConditionSourceLabels(candidate);
    if (labels.length > 0) {
      next.requiresLabel = labelOverride != null && labels.includes(labelOverride) ? labelOverride : labels[0];
    }
    return next;
  };
  const isConditionalEnabled = Boolean(question.conditional);
  const canEnableConditional = conditionalCandidatesWithOptions.length > 0;

  const applyConditionalChange = (nextConditional, changeOptions = {}) => {
    if (!onConditionalChange) return;
    onConditionalChange(question.id, nextConditional, changeOptions);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeout.current) {
        clearTimeout(typingTimeout.current);
      }
    };
  }, []);

  // Initialize content when not typing
  useEffect(() => {
    if (!isTyping.current) {
      if (textRef.current && textRef.current.innerHTML !== question.text) {
        textRef.current.innerHTML = question.text || '';
      }
      if (descriptionRef.current && descriptionRef.current.innerHTML !== question.description) {
        descriptionRef.current.innerHTML = question.description || '';
      }
    }
  }, [question.text, question.description]);

  // Debounced update function to prevent re-renders while typing
  const debouncedUpdate = (field, value) => {
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
    }
    typingTimeout.current = setTimeout(() => {
      updateQuestion(question.id, { [field]: value });
      isTyping.current = false;
    }, 300);
  };

  const applyFormat = (ref, field, tag) => {
    const element = ref.current;
    if (!element) return;

    if (element.contentEditable === 'true') {
      if (tag === 'b') document.execCommand('bold', false, null);
      if (tag === 'i') document.execCommand('italic', false, null);
      if (tag === 'u') document.execCommand('underline', false, null);

      setTimeout(() => {
        const htmlContent = element.innerHTML;
        updateQuestion(question.id, { [field]: htmlContent });
      }, 10);
    } else {
      const start = element.selectionStart; const end = element.selectionEnd;
      const selectedText = element.value.substring(start, end);
      const before = element.value.substring(0, start); const after = element.value.substring(end);
      if (selectedText) {
        const newText = `${before}<${tag}>${selectedText}</${tag}>${after}`;
        updateQuestion(question.id, { [field]: newText });
      }
    }
  };

  // Compute a friendly display name for the current image
  const imageDisplayName = (() => {
    if (question.file && question.file.name) return question.file.name;
    if (question.imageUrl) {
      try {
        const url = new URL(question.imageUrl);
        const pathParts = url.pathname.split('/');
        const filename = pathParts[pathParts.length - 1];
        if (filename && filename !== url.pathname) {
          return filename;
        }
        const match = url.pathname.match(/\/([^/]+\.(png|jpg|jpeg|gif|webp))$/i);
        if (match && match[1]) {
          return match[1];
        }
        const extMatch = url.pathname.match(/\.(png|jpg|jpeg|gif|webp)$/i);
        const ext = extMatch ? extMatch[1] : 'jpg';
        return `bild.${ext}`;
      } catch (e) {
        const match = question.imageUrl.match(/\/([^/]+\.(png|jpg|jpeg|gif|webp))$/i);
        if (match && match[1]) {
          return match[1];
        }
        return 'bild.jpg';
      }
    }
    if (question.imageDataUrl) {
      const match = /^data:image\/([^;]+)/i.exec(question.imageDataUrl);
      const ext = match ? match[1].toLowerCase() : 'png';
      return `vorlage-bild.${ext}`;
    }
    return null;
  })();

  return (
    <div
      ref={cardRef}
      data-question-id={question.id}
      className={`bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 p-4 sm:p-5 rounded-xl`}
    >
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <div
            draggable
            onDragStart={(e) => onDragStart(e, index)}
            onDragEnd={onDragEnd}
            className="drag-handle flex flex-col items-center text-slate-400 dark:text-slate-500 cursor-grab active:cursor-grabbing hover:text-slate-600 dark:hover:text-slate-300 transition-colors duration-200 p-1 rounded"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 7a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 15a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 19a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM15 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM15 7a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM15 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM15 15a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM15 19a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
            </svg>
          </div>
          <span className="font-bold text-base sm:text-lg text-slate-700 dark:text-slate-200">Frage {displayLabel || (displayIndex + 1)}</span>
          <select
            value={question.type}
            onChange={(e) => updateQuestion(question.id, { type: e.target.value })}
            onMouseDown={(e) => {
              e.stopPropagation();
              setIsDropdownOpen(true);
            }}
            onDragStart={(e) => e.stopPropagation()}
            onFocus={() => setIsDropdownOpen(true)}
            onBlur={() => setIsDropdownOpen(false)}
            className="dropdown-select p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm sm:text-base"
          >
            <option value="text">Text</option>
            <option value="single-choice">Single-Choice</option>
            <option value="multiple-choice">Multiple-Choice</option>
            <option value="ranking">Ranking</option>
            <option value="matrix">Matrix</option>
            <option value="combobox">Combobox</option>
            <option value="image">Bild</option>
            <option value="file-upload">Datei-Upload</option>
          </select>
        </div>
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min="0"
              step="1"
              value={question.maxScore ?? ''}
              onChange={(e) => {
                const rawValue = e.target.value;
                if (rawValue === '') {
                  updateQuestion(question.id, { maxScore: null });
                  return;
                }
                const numericValue = Number(rawValue);
                updateQuestion(question.id, { maxScore: Number.isFinite(numericValue) ? Math.max(0, Math.round(numericValue)) : 0 });
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => e.stopPropagation()}
              className="w-20 py-1 px-2 border border-amber-300 dark:border-amber-600 rounded-md text-sm text-center bg-amber-50 dark:bg-amber-900/20 text-slate-800 dark:text-slate-200"
              placeholder="Score"
              title="Maximalpunkte für diese Frage"
            />
          </div>
          <div className="w-px h-6 bg-slate-300 dark:bg-slate-600" />
          <button
            onClick={() => updateQuestion(question.id, { important: !question.important })}
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.stopPropagation()}
            className={`p-2 rounded-full transition-colors duration-200 ${question.important ? 'text-amber-500 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/20' : 'text-slate-400 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-amber-500 dark:hover:text-amber-400'}`}
            title={question.important ? 'Als wichtig markiert' : 'Als wichtig markieren'}
          >
            {question.important ? <StarSolidIcon /> : <StarIcon />}
          </button>
          <button
            onClick={() => updateQuestion(question.id, { required: question.required === false ? true : false })}
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.stopPropagation()}
            className={`p-2 rounded-full transition-colors duration-200 ${question.required === false ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/20' : 'text-slate-400 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-emerald-600 dark:hover:text-emerald-400'}`}
            title={question.required === false ? 'Freiwillige Antwort (blockiert „Weiter" nicht)' : 'Als freiwillig markieren'}
            aria-pressed={question.required === false}
          >
            {question.required === false ? (
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 1.5c2.9 0 5.25 2.35 5.25 5.25v3.75a.75.75 0 01-1.5 0V6.75a3.75 3.75 0 10-7.5 0v3a3 3 0 013 3v6.75a3 3 0 01-3 3H3.75a3 3 0 01-3-3v-6.75a3 3 0 013-3h9V6.75A5.25 5.25 0 0118 1.5z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path fillRule="evenodd" clipRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" />
              </svg>
            )}
          </button>
          <button
            onClick={() => setAdminNoteOpen(!adminNoteOpen)}
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.stopPropagation()}
            className={`p-2 rounded-full transition-colors duration-200 ${question.adminNote ? 'text-blue-500 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/20' : 'text-slate-400 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-blue-500 dark:hover:text-blue-400'}`}
            title="Interne Notiz"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => removeQuestion(question.id)}
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.stopPropagation()}
            className="p-2 text-slate-400 dark:text-slate-300 hover:bg-red-100 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-full"
          >
            <TrashIcon />
          </button>
        </div>
      </div>
      {pageOptions.length > 0 && (
        <div className="mb-3">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Frage-Seite
          </label>
          <select
            value={question.pageId || pageOptions[0]?.id || ''}
            onChange={(e) => onMoveToPage && onMoveToPage(question.id, e.target.value)}
            className="p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
          >
            {pageOptions.map((page) => (
              <option key={page.id} value={page.id}>
                {page.title}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="mb-3">
        <button
          type="button"
          onClick={() => setConditionalOpen(!conditionalOpen)}
          onMouseDown={(e) => e.stopPropagation()}
          onDragStart={(e) => e.stopPropagation()}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
            isConditionalEnabled
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
          }`}
          title="Bedingte Anzeige"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
          </svg>
          Bedingte Anzeige
          {isConditionalEnabled && (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
            </svg>
          )}
        </button>
        {conditionalOpen && (
          <div className="mt-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 p-3">
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Zeigt diese Frage nur an, wenn bei einer Frage mit Single-Choice- oder Combobox-Antwortformat auf dieser Seite eine bestimmte Option gewählt wurde.
            </p>
            <label className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors duration-200 ${
              isConditionalEnabled
                ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-600'
                : 'bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700'
            } ${(!isConditionalEnabled && !canEnableConditional) ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <input
                type="checkbox"
                className={`toggle toggle-lg ${isConditionalEnabled ? 'toggle-primary' : ''}`}
                checked={isConditionalEnabled}
                aria-label="Bedingte Anzeige umschalten"
                disabled={!isConditionalEnabled && !canEnableConditional}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  if (!e.target.checked) {
                    applyConditionalChange(null);
                    return;
                  }
                  const seeded = makeConditional(preferredConditionalCandidate);
                  if (!seeded) return;
                  applyConditionalChange(seeded, { skipReorder: true });
                }}
                onDragStart={(e) => e.stopPropagation()}
              />
              <span className={`text-sm font-semibold ${isConditionalEnabled ? 'text-blue-700 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'}`}>
                {isConditionalEnabled ? 'An' : 'Aus'}
              </span>
            </label>
            {isConditionalEnabled && (
              <div className={`mt-3 grid grid-cols-1 gap-2 ${selectedConditionalLabels.length > 0 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                <select
                  value={selectedConditionalQuestion ? String(selectedConditionalQuestion.id) : ''}
                  onChange={(e) => {
                    const nextCandidate = normalizedConditionalCandidates.find(
                      (candidate) => String(candidate.id) === String(e.target.value)
                    );
                    const seeded = makeConditional(nextCandidate);
                    if (!seeded) return;
                    applyConditionalChange(seeded);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onDragStart={(e) => e.stopPropagation()}
                  className="p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
                >
                  {normalizedConditionalCandidates.map((candidate) => (
                    <option key={candidate.id} value={candidate.id} disabled={!isCandidateUsable(candidate)}>
                      {labelMap[candidate.id] ? `Frage ${labelMap[candidate.id]}: ` : ''}{(candidate.text || '').replace(/<[^>]*>/g, '').trim() || 'Ohne Titel'}
                    </option>
                  ))}
                </select>
                {selectedConditionalLabels.length > 0 && (
                  <select
                    value={question.conditional.requiresLabel || selectedConditionalLabels[0]}
                    onChange={(e) => {
                      const next = makeConditional(
                        selectedConditionalQuestion,
                        question.conditional.requiresValue,
                        e.target.value
                      );
                      if (next) applyConditionalChange(next);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    className="p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
                  >
                    {selectedConditionalLabels.map((label, labelIndex) => (
                      <option key={`${label}_${labelIndex}`} value={label}>
                        {label}
                      </option>
                    ))}
                  </select>
                )}
                <select
                  value={question.conditional.requiresValue || (selectedConditionalOptions[0] || '')}
                  disabled={selectedConditionalOptions.length === 0}
                  onChange={(e) => {
                    const next = makeConditional(
                      selectedConditionalQuestion,
                      e.target.value,
                      question.conditional.requiresLabel
                    );
                    if (next) applyConditionalChange(next);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onDragStart={(e) => e.stopPropagation()}
                  className="p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
                >
                  {selectedConditionalOptions.length === 0 && (
                    <option value="">Bitte zuerst Optionen in der Zielfrage anlegen</option>
                  )}
                  {selectedConditionalOptions.map((option, optionIndex) => (
                    <option key={`${option}_${optionIndex}`} value={option}>
                      Wenn Antwort = {option}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {!question.conditional && normalizedConditionalCandidates.length === 0 && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-300">
                Dafür wird zuerst eine Frage mit Single-Choice- oder Combobox-Antwortformat auf dieser Seite benötigt.
              </p>
            )}
            {!question.conditional && normalizedConditionalCandidates.length > 0 && conditionalCandidatesWithOptions.length === 0 && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-300">
                Fügen Sie in der Zielfrage mindestens eine Option hinzu, um die Bedingung zu aktivieren.
              </p>
            )}
          </div>
        )}
      </div>
      <FormattingToolbar onFormat={(tag) => applyFormat(textRef, 'text', tag)} />
      <div
        ref={textRef}
        contentEditable={true}
        suppressContentEditableWarning={true}
        onInput={(e) => {
          isTyping.current = true;
          debouncedUpdate('text', e.target.innerHTML);
        }}
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
        }}
        onMouseDown={(e) => {
          if (e.button === 0) {
            e.stopPropagation();
          }
        }}
        onClick={() => { setDraggedIndex(null); setDragOverIndex(null); }}
        onDragStart={(e) => e.stopPropagation()}
        onBlur={(e) => {
          if (isDropdownOpen) return;

          const relatedTarget = e.relatedTarget;
          if (relatedTarget && (
            relatedTarget.tagName === 'SELECT' ||
            relatedTarget.tagName === 'BUTTON' ||
            relatedTarget.closest('button') ||
            relatedTarget.closest('select')
          )) {
            return;
          }

          if (typingTimeout.current) {
            clearTimeout(typingTimeout.current);
          }
          updateQuestion(question.id, { text: e.target.innerHTML });
          isTyping.current = false;
        }}
        className="content-editable-field w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500 mb-2 text-base bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 min-h-[60px] focus:outline-none"
        data-placeholder={question.type === 'image' ? 'Bildbeschreibung oder Frage...' : 'Deine Frage hier...'}
        style={{
          minHeight: '60px'
        }}

      />

      {/* Text Limit for Text Questions */}
      {question.type === 'text' && (
        <div className="mb-3">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Zeichenlimit:
          </label>
          <select
            value={question.wordLimit || 'no-limit'}
            onChange={(e) => updateQuestion(question.id, { wordLimit: e.target.value })}
            className="p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
          >
            <option value="no-limit">Kein Limit</option>
            <option value="50">50 Zeichen</option>
            <option value="100">100 Zeichen</option>
            <option value="200">200 Zeichen</option>
            <option value="300">300 Zeichen</option>
            <option value="500">500 Zeichen</option>
            <option value="1000">1000 Zeichen</option>
          </select>
        </div>
      )}

      <FormattingToolbar onFormat={(tag) => applyFormat(descriptionRef, 'description', tag)} />
      <div
        ref={descriptionRef}
        contentEditable={true}
        suppressContentEditableWarning={true}
        onInput={(e) => {
          isTyping.current = true;
          debouncedUpdate('description', e.target.innerHTML);
        }}
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
        }}
        onBlur={(e) => {
          if (typingTimeout.current) {
            clearTimeout(typingTimeout.current);
          }
          updateQuestion(question.id, { description: e.target.innerHTML });
          isTyping.current = false;
        }}
        className="content-editable-field w-full p-2 text-sm sm:text-base border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-1 focus:ring-green-400 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 min-h-[40px] focus:outline-none"
        data-placeholder="Zusätzliche Beschreibung oder Anweisungen (optional)"
        style={{
          minHeight: '40px'
        }}

      />
      {(question.type === 'single-choice' || question.type === 'multiple-choice') && (
        <div className="question-options mt-4 pl-4 border-l-2 border-green-200 dark:border-green-700">
          <h4 className="font-semibold text-slate-600 dark:text-slate-300 mb-2">Antwortoptionen ({question.type === 'single-choice' ? 'Single-Choice' : 'Multiple-Choice'})</h4>

          {/* Max Selections for Multiple Choice */}
          {question.type === 'multiple-choice' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Maximale Anzahl auswählbarer Antworten:
              </label>
              <select
                value={question.maxSelections || 'unlimited'}
                onChange={(e) => updateQuestion(question.id, { maxSelections: e.target.value === 'unlimited' ? null : parseInt(e.target.value) })}
                className="p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
              >
                <option value="unlimited">Unbegrenzt</option>
                <option value="1">1 Antwort</option>
                <option value="2">2 Antworten</option>
                <option value="3">3 Antworten</option>
                <option value="4">4 Antworten</option>
                <option value="5">5 Antworten</option>
                <option value="6">6 Antworten</option>
                <option value="7">7 Antworten</option>
                <option value="8">8 Antworten</option>
                <option value="9">9 Antworten</option>
                <option value="10">10 Antworten</option>
              </select>
            </div>
          )}

          <div className="space-y-2">
            {(question.options || []).map((opt, optIndex) => {
              const isCorrect = (question.correctOptions || []).includes(optIndex);
              return (
              <div
                key={optIndex}
                className={`flex items-center gap-2 transition-all duration-200 ${draggedOptionIndex === optIndex ? 'opacity-50 scale-95' : ''
                  } ${dragOverOptionIndex === optIndex && draggedOptionIndex !== optIndex ? 'border-2 border-dashed border-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg' : ''
                  }`}
                onDragOver={(e) => handleOptionDragOver(e, question.id, optIndex)}
                onDragEnd={() => handleOptionDragEnd(question.id)}
              >
                <div
                  draggable
                  onDragStart={() => handleOptionDragStart(question.id, optIndex)}
                  onDragEnd={() => handleOptionDragEnd(question.id)}
                  className="drag-handle cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                  </svg>
                </div>
                <div className={`flex items-center gap-2 grow p-2 rounded-lg border transition-colors ${
                  isCorrect
                    ? 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                    : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800'
                }`}>
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => handleOptionChange(question.id, optIndex, e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    onClick={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                    className="option-input grow p-1.5 border-0 bg-transparent text-base text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-0"
                    placeholder="Option eingeben..."
                  />
                  {handleOptionScoreChange && (
                    <input
                      type="number"
                      min="0"
                      value={(question.optionScores || [])[optIndex] ?? ''}
                      onChange={(e) => handleOptionScoreChange(question.id, optIndex, e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.stopPropagation()}
                      className="w-16 p-1.5 border border-amber-300 dark:border-amber-600 rounded-md text-sm text-center bg-amber-50 dark:bg-amber-900/20 text-slate-800 dark:text-slate-200 shrink-0"
                      placeholder="Pkt"
                      title="Punkte für diese Option"
                    />
                  )}
                  <div className="w-px h-6 bg-slate-200 dark:bg-slate-600 shrink-0" />
                  <button
                    onClick={() => {
                      const current = question.correctOptions || [];
                      const next = current.includes(optIndex)
                        ? current.filter(i => i !== optIndex)
                        : [...current, optIndex];
                      updateQuestion(question.id, { correctOptions: next });
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    className={`shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${
                      isCorrect
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 dark:text-slate-500'
                    }`}
                    title={isCorrect ? 'Als korrekt markiert' : 'Als korrekt markieren'}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      isCorrect
                        ? 'border-green-600 bg-green-600 dark:border-green-500 dark:bg-green-500'
                        : 'border-slate-300 dark:border-slate-500 hover:border-green-400 dark:hover:border-green-400'
                    }`}>
                      {isCorrect && (
                        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-xs font-medium hidden sm:inline ${
                      isCorrect ? 'text-green-700 dark:text-green-300' : 'text-slate-400 dark:text-slate-500'
                    }`}>Richtig</span>
                  </button>
                </div>
                <button onClick={() => removeOption(question.id, optIndex)} onDragStart={(e) => e.stopPropagation()} className="p-2 text-slate-400 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 rounded-full shrink-0"><MinusCircleIcon /></button>
              </div>
              );
            })}
            <button onClick={() => addOption(question.id)} onDragStart={(e) => e.stopPropagation()} className="mt-2 w-full px-3 py-2 text-sm sm:text-base bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 font-semibold rounded-md hover:bg-green-200 dark:hover:bg-green-900/50">+ Option hinzufügen</button>
          </div>
        </div>
      )}

      {question.type === 'matrix' && (
        <div className="question-options mt-4 pl-4 border-l-2 border-purple-200 dark:border-purple-700">
          <h4 className="font-semibold text-slate-600 dark:text-slate-300 mb-2">Matrix-Konfiguration</h4>

          {/* Mode toggle */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Zeilen-Modus</label>
            <select
              value={question.matrixMode || 'predefined'}
              onChange={(e) => updateQuestion(question.id, { matrixMode: e.target.value })}
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => e.stopPropagation()}
              className="p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
            >
              <option value="dynamic">Dynamisch (Bewerber fügt Zeilen hinzu)</option>
              <option value="predefined">Vorgegeben (feste Zeilen, nicht änderbar)</option>
            </select>
          </div>

          {/* Selection mode toggle */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Auswahlmodus</label>
            <select
              value={question.matrixSelectionMode || 'per-row'}
              onChange={(e) => updateQuestion(question.id, { matrixSelectionMode: e.target.value })}
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => e.stopPropagation()}
              className="p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
            >
              <option value="per-row">Eine Auswahl pro Zeile</option>
              <option value="single">Nur eine Auswahl über gesamte Matrix</option>
            </select>
          </div>

          {/* Dynamic mode: row placeholder */}
          {(question.matrixMode || 'predefined') === 'dynamic' && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Platzhalter für Zeilen-Label
              </label>
              <input
                type="text"
                value={question.rowPlaceholder || ''}
                onChange={(e) => updateQuestion(question.id, { rowPlaceholder: e.target.value })}
                onMouseDown={(e) => e.stopPropagation()}
                onDragStart={(e) => e.stopPropagation()}
                className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                placeholder="z.B. Sprache, Kompetenz..."
              />
            </div>
          )}

          {/* Predefined mode: row labels editor */}
          {question.matrixMode === 'predefined' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Zeilen-Labels</label>
              <div className="space-y-2">
                {(question.rowLabels || []).map((label, labelIndex) => (
                  <div key={labelIndex} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={label}
                      onChange={(e) => {
                        const newLabels = [...(question.rowLabels || [])];
                        newLabels[labelIndex] = e.target.value;
                        updateQuestion(question.id, { rowLabels: newLabels });
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.stopPropagation()}
                      className="grow p-2 border border-slate-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                      placeholder="Zeilen-Label eingeben..."
                    />
                    <button
                      onClick={() => {
                        const newLabels = (question.rowLabels || []).filter((_, i) => i !== labelIndex);
                        updateQuestion(question.id, { rowLabels: newLabels });
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.stopPropagation()}
                      className="p-2 text-slate-400 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 rounded-full"
                    >
                      <MinusCircleIcon />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => updateQuestion(question.id, { rowLabels: [...(question.rowLabels || []), ''] })}
                  onMouseDown={(e) => e.stopPropagation()}
                  onDragStart={(e) => e.stopPropagation()}
                  className="mt-1 px-3 py-2 text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 font-semibold rounded-md hover:bg-purple-200 dark:hover:bg-purple-900/50"
                >
                  + Zeile
                </button>
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <input
                  type="checkbox"
                  checked={Boolean(question.matrixAllowAdditionalRows)}
                  onChange={(e) => updateQuestion(question.id, { matrixAllowAdditionalRows: e.target.checked })}
                  onMouseDown={(e) => e.stopPropagation()}
                  onDragStart={(e) => e.stopPropagation()}
                  className="h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                />
                Bewerber dürfen zusätzliche Zeilen hinzufügen
              </label>
            </div>
          )}

          {/* Column options (shared by both modes) */}
          <h4 className="font-semibold text-slate-600 dark:text-slate-300 mb-2 mt-4">Spaltenoptionen (Matrix)</h4>
          <div className="space-y-2">
            {(question.options || []).map((opt, optIndex) => {
              const isCorrect = (question.correctOptions || []).includes(optIndex);
              return (
              <div
                key={optIndex}
                className={`flex items-center gap-2 transition-all duration-200 ${draggedOptionIndex === optIndex ? 'opacity-50 scale-95' : ''
                  } ${dragOverOptionIndex === optIndex && draggedOptionIndex !== optIndex ? 'border-2 border-dashed border-purple-400 bg-purple-50 dark:bg-purple-900/20 rounded-lg' : ''
                  }`}
                onDragOver={(e) => handleOptionDragOver(e, question.id, optIndex)}
                onDragEnd={() => handleOptionDragEnd(question.id)}
              >
                <div
                  draggable
                  onDragStart={() => handleOptionDragStart(question.id, optIndex)}
                  onDragEnd={() => handleOptionDragEnd(question.id)}
                  className="drag-handle cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                  </svg>
                </div>
                <div className={`flex items-center gap-2 grow p-2 rounded-lg border transition-colors ${
                  isCorrect
                    ? 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                    : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800'
                }`}>
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => handleOptionChange(question.id, optIndex, e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    onClick={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                    className="option-input grow p-1.5 border-0 bg-transparent text-base text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-0"
                    placeholder="Spaltenoption eingeben..."
                  />
                  {handleOptionScoreChange && (
                    <input
                      type="number"
                      min="0"
                      value={(question.optionScores || [])[optIndex] ?? ''}
                      onChange={(e) => handleOptionScoreChange(question.id, optIndex, e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.stopPropagation()}
                      className="w-16 p-1.5 border border-amber-300 dark:border-amber-600 rounded-md text-sm text-center bg-amber-50 dark:bg-amber-900/20 text-slate-800 dark:text-slate-200 shrink-0"
                      placeholder="Pkt"
                      title="Punkte für diese Spaltenoption"
                    />
                  )}
                  <div className="w-px h-6 bg-slate-200 dark:bg-slate-600 shrink-0" />
                  <button
                    onClick={() => {
                      const current = question.correctOptions || [];
                      const next = current.includes(optIndex)
                        ? current.filter(i => i !== optIndex)
                        : [...current, optIndex];
                      updateQuestion(question.id, { correctOptions: next });
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    className={`shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${
                      isCorrect
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 dark:text-slate-500'
                    }`}
                    title={isCorrect ? 'Als korrekt markiert' : 'Als korrekt markieren'}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      isCorrect
                        ? 'border-green-600 bg-green-600 dark:border-green-500 dark:bg-green-500'
                        : 'border-slate-300 dark:border-slate-500 hover:border-green-400 dark:hover:border-green-400'
                    }`}>
                      {isCorrect && (
                        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-xs font-medium hidden sm:inline ${
                      isCorrect ? 'text-green-700 dark:text-green-300' : 'text-slate-400 dark:text-slate-500'
                    }`}>Richtig</span>
                  </button>
                </div>
                <button onClick={() => removeOption(question.id, optIndex)} onDragStart={(e) => e.stopPropagation()} className="p-2 text-slate-400 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 rounded-full shrink-0"><MinusCircleIcon /></button>
              </div>
              );
            })}
            <button onClick={() => addOption(question.id)} onDragStart={(e) => e.stopPropagation()} className="mt-2 w-full px-3 py-2 text-sm sm:text-base bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 font-semibold rounded-md hover:bg-purple-200 dark:hover:bg-purple-900/50">+ Spaltenoption hinzufügen</button>
          </div>
        </div>
      )}

      {question.type === 'combobox' && (
        <div className="question-options mt-4 pl-4 border-l-2 border-teal-200 dark:border-teal-700">
          <h4 className="font-semibold text-slate-600 dark:text-slate-300 mb-2">Combobox-Konfiguration</h4>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Eintrags-Modus
                </label>
                <select
                  value={question.comboboxMode || 'predefined'}
                  onChange={(e) => updateQuestion(question.id, { comboboxMode: e.target.value })}
                  onMouseDown={(e) => e.stopPropagation()}
                  onDragStart={(e) => e.stopPropagation()}
                  className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                >
                  <option value="dynamic">Frei (Bewerber gibt Eintrag ein)</option>
                  <option value="predefined">Vorgegebene Einträge nicht änderbar</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Dropdown-Label
                </label>
                <input
                  type="text"
                  value={question.columnLabel || ''}
                  onChange={(e) => updateQuestion(question.id, { columnLabel: e.target.value })}
                  onMouseDown={(e) => e.stopPropagation()}
                  onDragStart={(e) => e.stopPropagation()}
                  className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                  placeholder="z.B. Niveau"
                />
              </div>
            </div>

            {(question.comboboxMode || 'predefined') === 'dynamic' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Eingabe-Label
                  </label>
                  <input
                    type="text"
                    value={question.rowInputLabel || ''}
                    onChange={(e) => updateQuestion(question.id, { rowInputLabel: e.target.value })}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                    placeholder="z.B. Sprache"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Eingabe-Platzhalter
                  </label>
                  <input
                    type="text"
                    value={question.rowPlaceholder || ''}
                    onChange={(e) => updateQuestion(question.id, { rowPlaceholder: e.target.value })}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                    placeholder="z.B. Sprache eingeben..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Vorschläge (Autocomplete)
                  </label>
                  <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">Bewerber können auch Freitext eingeben.</p>
                  <div className="space-y-2">
                    {(question.suggestions || []).map((suggestion, sugIndex) => (
                      <div key={sugIndex} className="flex items-center gap-2">
                        <div className="flex items-center gap-2 grow">
                          <div
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', sugIndex);
                              e.currentTarget.closest('.space-y-2').dataset.draggingSuggestion = sugIndex;
                            }}
                            onDragEnd={(e) => {
                              delete e.currentTarget.closest('.space-y-2').dataset.draggingSuggestion;
                            }}
                            className="drag-handle cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                            </svg>
                          </div>
                          <input
                            type="text"
                            value={suggestion}
                            onChange={(e) => {
                              const newSuggestions = [...(question.suggestions || [])];
                              newSuggestions[sugIndex] = e.target.value;
                              updateQuestion(question.id, { suggestions: newSuggestions });
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onDragStart={(e) => e.stopPropagation()}
                            className="grow p-2 border border-slate-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                            placeholder="Vorschlag eingeben..."
                          />
                        </div>
                        <button
                          onClick={() => {
                            const newSuggestions = (question.suggestions || []).filter((_, i) => i !== sugIndex);
                            updateQuestion(question.id, { suggestions: newSuggestions });
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onDragStart={(e) => e.stopPropagation()}
                          className="p-2 text-slate-400 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 rounded-full"
                        >
                          <MinusCircleIcon />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => updateQuestion(question.id, { suggestions: [...(question.suggestions || []), ''] })}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.stopPropagation()}
                      className="mt-1 px-3 py-2 text-sm bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 font-semibold rounded-md hover:bg-teal-200 dark:hover:bg-teal-900/50"
                    >
                      + Vorschlag
                    </button>
                  </div>
                </div>
              </>
            )}

            {(question.comboboxMode || 'predefined') === 'predefined' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Vorgegebene Einträge
                </label>
                <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                  Diese Einträge erscheinen im Formular als feste Zeilen und können von Bewerbern nicht verändert werden.
                </p>
                <div className="space-y-2">
                  {(question.predefinedEntries || []).map((entry, entryIndex) => (
                    <div key={entryIndex} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={entry}
                        onChange={(e) => {
                          const newEntries = [...(question.predefinedEntries || [])];
                          newEntries[entryIndex] = e.target.value;
                          updateQuestion(question.id, { predefinedEntries: newEntries });
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onDragStart={(e) => e.stopPropagation()}
                        className="grow p-2 border border-slate-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                        placeholder="Eintrag eingeben..."
                      />
                      <button
                        onClick={() => {
                          const newEntries = (question.predefinedEntries || []).filter((_, i) => i !== entryIndex);
                          updateQuestion(question.id, { predefinedEntries: newEntries });
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onDragStart={(e) => e.stopPropagation()}
                        className="p-2 text-slate-400 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 rounded-full"
                      >
                        <MinusCircleIcon />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => updateQuestion(question.id, { predefinedEntries: [...(question.predefinedEntries || []), ''] })}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    className="mt-1 px-3 py-2 text-sm bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 font-semibold rounded-md hover:bg-teal-200 dark:hover:bg-teal-900/50"
                  >
                    + Eintrag
                  </button>
                </div>
                <label className="mt-3 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={Boolean(question.comboboxAllowAdditionalEntries)}
                    onChange={(e) => updateQuestion(question.id, { comboboxAllowAdditionalEntries: e.target.checked })}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  Bewerber dürfen zusätzliche Einträge hinzufügen
                </label>
              </div>
            )}
          </div>

          <h4 className="font-semibold text-slate-600 dark:text-slate-300 mb-2 mt-4">Dropdown-Optionen (Combobox)</h4>
          <div className="space-y-2">
            {(question.options || []).map((opt, optIndex) => {
              const isCorrect = (question.correctOptions || []).includes(optIndex);
              return (
              <div
                key={optIndex}
                className={`flex items-center gap-2 transition-all duration-200 ${draggedOptionIndex === optIndex ? 'opacity-50 scale-95' : ''
                  } ${dragOverOptionIndex === optIndex && draggedOptionIndex !== optIndex ? 'border-2 border-dashed border-teal-400 bg-teal-50 dark:bg-teal-900/20 rounded-lg' : ''
                  }`}
                onDragOver={(e) => handleOptionDragOver(e, question.id, optIndex)}
                onDragEnd={() => handleOptionDragEnd(question.id)}
              >
                <div
                  draggable
                  onDragStart={() => handleOptionDragStart(question.id, optIndex)}
                  onDragEnd={() => handleOptionDragEnd(question.id)}
                  className="drag-handle cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                  </svg>
                </div>
                <div className={`flex items-center gap-2 grow p-2 rounded-lg border transition-colors ${
                  isCorrect
                    ? 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                    : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800'
                }`}>
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => handleOptionChange(question.id, optIndex, e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    onClick={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                    className="option-input grow p-1.5 border-0 bg-transparent text-base text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-0"
                    placeholder="Dropdown-Option eingeben..."
                  />
                  {handleOptionScoreChange && (
                    <input
                      type="number"
                      min="0"
                      value={(question.optionScores || [])[optIndex] ?? ''}
                      onChange={(e) => handleOptionScoreChange(question.id, optIndex, e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.stopPropagation()}
                      className="w-16 p-1.5 border border-amber-300 dark:border-amber-600 rounded-md text-sm text-center bg-amber-50 dark:bg-amber-900/20 text-slate-800 dark:text-slate-200 shrink-0"
                      placeholder="Pkt"
                      title="Punkte für diese Option"
                    />
                  )}
                  <div className="w-px h-6 bg-slate-200 dark:bg-slate-600 shrink-0" />
                  <button
                    onClick={() => {
                      const current = question.correctOptions || [];
                      const next = current.includes(optIndex)
                        ? current.filter(i => i !== optIndex)
                        : [...current, optIndex];
                      updateQuestion(question.id, { correctOptions: next });
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    className={`shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${
                      isCorrect
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 dark:text-slate-500'
                    }`}
                    title={isCorrect ? 'Als korrekt markiert' : 'Als korrekt markieren'}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      isCorrect
                        ? 'border-green-600 bg-green-600 dark:border-green-500 dark:bg-green-500'
                        : 'border-slate-300 dark:border-slate-500 hover:border-green-400 dark:hover:border-green-400'
                    }`}>
                      {isCorrect && (
                        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-xs font-medium hidden sm:inline ${
                      isCorrect ? 'text-green-700 dark:text-green-300' : 'text-slate-400 dark:text-slate-500'
                    }`}>Richtig</span>
                  </button>
                </div>
                <button onClick={() => removeOption(question.id, optIndex)} onDragStart={(e) => e.stopPropagation()} className="p-2 text-slate-400 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 rounded-full shrink-0"><MinusCircleIcon /></button>
              </div>
              );
            })}
            <button onClick={() => addOption(question.id)} onDragStart={(e) => e.stopPropagation()} className="mt-2 w-full px-3 py-2 text-sm sm:text-base bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 font-semibold rounded-md hover:bg-teal-200 dark:hover:bg-teal-900/50">+ Dropdown-Option hinzufügen</button>
          </div>
        </div>
      )}

      {question.type === 'ranking' && (
        <div className="question-options mt-4 pl-4 border-l-2 border-blue-200 dark:border-blue-700">
          <h4 className="font-semibold text-slate-600 dark:text-slate-300 mb-2">Antwortoptionen (Ranking)</h4>
          <div className="space-y-2">
            {(question.options || []).map((opt, optIndex) => {
              const isSelected = (question.correctOptions || []).includes(optIndex);
              const rank = isSelected ? [...(question.correctOptions || [])].sort((a, b) => a - b).indexOf(optIndex) + 1 : null;
              return (
              <div
                key={optIndex}
                className={`flex items-center gap-2 transition-all duration-200 ${draggedOptionIndex === optIndex ? 'opacity-50 scale-95' : ''
                  } ${dragOverOptionIndex === optIndex && draggedOptionIndex !== optIndex ? 'border-2 border-dashed border-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg' : ''
                  }`}
                onDragOver={(e) => handleOptionDragOver(e, question.id, optIndex)}
                onDragEnd={() => handleOptionDragEnd(question.id)}
              >
                <div
                  draggable
                  onDragStart={() => handleOptionDragStart(question.id, optIndex)}
                  onDragEnd={() => handleOptionDragEnd(question.id)}
                  className="drag-handle cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                  </svg>
                </div>
                <div className={`flex items-center gap-2 grow p-2 rounded-lg border transition-colors ${
                  isSelected
                    ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800'
                }`}>
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => handleOptionChange(question.id, optIndex, e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    onClick={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                    className="option-input grow p-1.5 border-0 bg-transparent text-base text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-0"
                    placeholder="Option eingeben..."
                  />
                  {handleOptionScoreChange && (
                    <input
                      type="number"
                      min="0"
                      value={(question.optionScores || [])[optIndex] ?? ''}
                      onChange={(e) => handleOptionScoreChange(question.id, optIndex, e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.stopPropagation()}
                      className="w-16 p-1.5 border border-amber-300 dark:border-amber-600 rounded-md text-sm text-center bg-amber-50 dark:bg-amber-900/20 text-slate-800 dark:text-slate-200 shrink-0"
                      placeholder="Pkt"
                      title="Punkte wenn an richtigem Rang"
                    />
                  )}
                  <div className="w-px h-6 bg-slate-200 dark:bg-slate-600 shrink-0" />
                  <button
                    onClick={() => {
                      const current = question.correctOptions || [];
                      const next = isSelected
                        ? current.filter(i => i !== optIndex)
                        : [...current, optIndex];
                      updateQuestion(question.id, { correctOptions: next });
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    className={`shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${
                      isSelected
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 dark:text-slate-500'
                    }`}
                    title={isSelected ? `Soll-Rang ${rank}` : 'Soll-Rang zuweisen'}
                  >
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors text-xs font-bold ${
                      isSelected
                        ? 'border-blue-600 bg-blue-600 dark:border-blue-500 dark:bg-blue-500 text-white'
                        : 'border-slate-300 dark:border-slate-500 hover:border-blue-400 dark:hover:border-blue-400 text-slate-400'
                    }`}>
                      {rank || '#'}
                    </div>
                    <span className={`text-xs font-medium hidden sm:inline ${
                      isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-slate-400 dark:text-slate-500'
                    }`}>Rang</span>
                  </button>
                </div>
                <button onClick={() => removeOption(question.id, optIndex)} onDragStart={(e) => e.stopPropagation()} className="p-2 text-slate-400 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 rounded-full shrink-0"><MinusCircleIcon /></button>
              </div>
              );
            })}
            <button
              onClick={() => addOption(question.id)}
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => e.stopPropagation()}
              className="mt-2 w-full px-3 py-2 text-sm sm:text-base bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 font-semibold rounded-md hover:bg-blue-200 dark:hover:bg-blue-900/50"
            >
              + Option hinzufügen
            </button>
          </div>
        </div>
      )}
      {question.type === 'image' && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Bilddatei hochladen (png, jpg, jpeg, gif, webp)</label>
          <div className="file-input-container flex items-center gap-2">
            {(question.imageDataUrl || question.file || question.imageUrl) && (
              <button
                type="button"
                onClick={() => {
                  updateQuestion(question.id, { file: null, imageDataUrl: null, imageUrl: null, imageId: null });
                  setUploadProgress(null);
                  setUploadSize(null);
                  setImageError(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onDragStart={(e) => e.stopPropagation()}
                className="px-2.5 py-1.5 rounded-md border border-red-400 dark:border-red-500 text-red-700 dark:text-red-300 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 font-semibold"
                title="Auswahl löschen"
                aria-label="Ausgewählte Datei entfernen"
              >
                ×
              </button>
            )}
            <label className="relative flex-1">
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                ref={fileInputRef}
                onMouseDown={(e) => e.stopPropagation()}
                onDragStart={(e) => e.stopPropagation()}
                className="file-input w-full pr-28 p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                title={imageDisplayName || ''}
                onChange={async (e) => {
                  const file = e.target.files?.[0] || null;
                  if (!file) {
                    updateQuestion(question.id, { file: null, imageDataUrl: null, imageUrl: null, imageId: null });
                    setUploadProgress(null);
                    setUploadSize(null);
                    setImageError(null);
                    return;
                  }

                  setImageError(null);

                  const isGif = file.type === 'image/gif';
                  const maxSize = isGif ? MAX_GIF_BYTES : MAX_IMAGE_BYTES;

                  if (file.size > maxSize) {
                    const kb = Math.round(file.size / 1024);
                    const maxKb = Math.round(maxSize / 1024);
                    setImageError(`Bild ist zu groß (${kb} KB). Limit: ${maxKb} KB${isGif ? ' (GIF)' : ''}.`);
                    return;
                  }

                  setUploadProgress(10);
                  setUploadSize(file.size);

                  try {
                    const previewDataUrl = await fileToDataUrl(file);
                    setUploadProgress(100);

                    updateQuestion(question.id, {
                      file,
                      imageDataUrl: previewDataUrl,
                      imageUrl: null,
                      imageId: null
                    });
                  } catch (error) {
                    console.error('Error creating preview:', error);
                    setImageError('Fehler beim Verarbeiten der Datei');
                    setUploadProgress(null);
                  }
                }}
              />
              {(uploadProgress != null || question.imageDataUrl || question.imageUrl) && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap flex items-center gap-1 bg-white/80 dark:bg-slate-800/80 px-2 py-0.5 rounded">
                  <span>{uploadProgress != null ? uploadProgress : 100}%</span>
                  {(() => {
                    const sz = uploadSize ?? (question.imageDataUrl ? dataUrlToBytes(question.imageDataUrl) : null);
                    if (sz == null) return null;
                    return <span className="opacity-80">· {(sz / 1024 / 1024) >= 1 ? (sz / 1024 / 1024).toFixed(2) + ' MB' : Math.round(sz / 1024) + ' KB'}</span>;
                  })()}
                  {imageDisplayName && <span className="opacity-80 max-w-[140px] truncate">· {imageDisplayName}</span>}
                  {question.imageUrl && <span className="opacity-80">· Hochgeladen</span>}
                  <svg className="h-4 w-4 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
              )}
            </label>
          </div>
          {/* Image size disclaimer and error display */}
          {question.type === 'image' && (
            <>
              <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                <div className="flex items-center gap-1">
                  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                  <span>Max. Größe: 10 MB (alle Bildformate)</span>
                </div>
              </div>
              {imageError && (
                <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md">
                  <p className="text-red-600 dark:text-red-400 text-sm font-medium">{imageError}</p>
                </div>
              )}
            </>
          )}
          {/* Image Preview Accordion */}
          {(question.imageDataUrl || question.imageUrl) && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => {
                  const currentState = question.imagePreviewExpanded || false;
                  updateQuestion(question.id, { imagePreviewExpanded: !currentState });
                }}
                className="flex items-center justify-between w-full p-3 text-left bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
              >
                <span className="font-medium text-slate-700 dark:text-slate-200 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Bildvorschau {question.imagePreviewExpanded ? 'ausblenden' : 'anzeigen'}
                </span>
                <svg
                  className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${question.imagePreviewExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {question.imagePreviewExpanded && (
                <div className="mt-3 p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg">
                  <img
                    src={question.imageUrl || question.imageDataUrl}
                    alt="Bildvorschau"
                    className="max-h-64 w-auto mx-auto rounded-lg border border-slate-200 dark:border-slate-700 object-contain"
                  />
                  <div className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
                    {question.imageUrl ? 'Bild hochgeladen' : (() => {
                      const sz = question.imageDataUrl ? dataUrlToBytes(question.imageDataUrl) : null;
                      if (sz == null) return null;
                      return `Dateigröße: ${(sz / 1024 / 1024) >= 1 ? (sz / 1024 / 1024).toFixed(2) + ' MB' : Math.round(sz / 1024) + ' KB'}`;
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          {question.type === 'file-upload' && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Erlaubte Dateitypen</label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { label: 'PDF', value: 'application/pdf' },
                    { label: 'DOCX', value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
                    { label: 'PNG', value: 'image/png' },
                    { label: 'JPG', value: 'image/jpeg' },
                    { label: 'WebP', value: 'image/webp' }
                  ].map(ft => {
                    const accepted = question.acceptedFileTypes || [];
                    const isChecked = accepted.includes(ft.value);
                    return (
                      <label key={ft.value} className="flex items-center gap-1.5 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            const next = isChecked
                              ? accepted.filter(t => t !== ft.value)
                              : [...accepted, ft.value];
                            updateQuestion(question.id, { acceptedFileTypes: next });
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        {ft.label}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Max. Dateigröße (MB)</label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={question.maxFileSize ?? 10}
                  onChange={(e) => updateQuestion(question.id, { maxFileSize: Math.max(1, Math.min(50, Number(e.target.value) || 10)) })}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="w-24 p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={question.requireNote ?? false}
                  onChange={(e) => updateQuestion(question.id, { requireNote: e.target.checked })}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">Notiz-Feld erforderlich</span>
              </label>
            </div>
          )}

          {question.type !== 'ranking' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Antwortformat</label>
              <select
                value={question.answerType || 'text'}
                onChange={(e) => updateQuestion(question.id, { answerType: e.target.value })}
                onMouseDown={(e) => e.stopPropagation()}
                onDragStart={(e) => e.stopPropagation()}
                className="dropdown-select p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
              >
                <option value="text">Text</option>
                <option value="single-choice">Single-Choice</option>
                <option value="multiple-choice">Multiple-Choice</option>
              </select>
            </div>
          )}
          {(question.answerType === 'single-choice' || question.answerType === 'multiple-choice') && (
            <div className="question-options mt-4 pl-4 border-l-2 border-green-200 dark:border-green-700">
              <h4 className="font-semibold text-slate-600 dark:text-slate-300 mb-2">Antwortoptionen ({question.answerType === 'single-choice' ? 'Single-Choice' : 'Multiple-Choice'})</h4>
              <div className="space-y-2">
                {(question.options || []).map((opt, optIndex) => (
                  <div key={optIndex} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => handleOptionChange(question.id, optIndex, e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.stopPropagation()}
                      className="option-input grow p-2 border border-slate-300 dark:border-slate-600 rounded-md text-base bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                    />
                    {handleOptionScoreChange && (
                      <input
                        type="number"
                        min="0"
                        value={(question.optionScores || [])[optIndex] ?? ''}
                        onChange={(e) => handleOptionScoreChange(question.id, optIndex, e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onDragStart={(e) => e.stopPropagation()}
                        className="w-16 p-2 border border-amber-300 dark:border-amber-600 rounded-md text-sm text-center bg-amber-50 dark:bg-amber-900/20 text-slate-800 dark:text-slate-200"
                        placeholder="Pkt"
                        title="Punkte für diese Option"
                      />
                    )}
                    <button
                      onClick={() => {
                        const current = question.correctOptions || [];
                        const next = current.includes(optIndex)
                          ? current.filter(i => i !== optIndex)
                          : [...current, optIndex];
                        updateQuestion(question.id, { correctOptions: next });
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.stopPropagation()}
                      className="p-1 transition-colors"
                      title={(question.correctOptions || []).includes(optIndex) ? 'Als korrekt markiert' : 'Als korrekt markieren'}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        (question.correctOptions || []).includes(optIndex)
                          ? 'border-green-600 bg-green-600 dark:border-green-500 dark:bg-green-500'
                          : 'border-slate-300 dark:border-slate-500 hover:border-green-400 dark:hover:border-green-400'
                      }`}>
                        {(question.correctOptions || []).includes(optIndex) && (
                          <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                    <button
                      onClick={() => removeOption(question.id, optIndex)}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.stopPropagation()}
                      className="p-2 text-slate-400 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 rounded-full"
                    >
                      <MinusCircleIcon />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addOption(question.id)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onDragStart={(e) => e.stopPropagation()}
                  className="mt-2 w-full px-3 py-2 text-sm sm:text-base bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 font-semibold rounded-md hover:bg-green-200 dark:hover:bg-green-900/50"
                >
                  + Option hinzufügen
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {imageDisplayName && (
        <div className="mt-1 text-xs text-slate-600 dark:text-slate-300 flex items-center gap-1">
          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
          <span className="opacity-80">Aktuelles Bild:</span>
          <span className="font-medium">{imageDisplayName}</span>
        </div>
      )}
      {adminNoteOpen && (
        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
          <label className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1 block">
            Interne Notiz
          </label>
          <textarea
            value={question.adminNote || ''}
            onChange={(e) => updateQuestion(question.id, { adminNote: e.target.value })}
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.stopPropagation()}
            placeholder="z.B. korrekte Antwort, Begründung, Hinweise für Kollegen..."
            rows={2}
            className="w-full p-2 text-sm border border-blue-200 dark:border-blue-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:ring-1 focus:ring-blue-400 focus:outline-none"
          />
        </div>
      )}
    </div>
  );
}
